import { useEffect, useState } from "react";
import { AlertCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  OtcContractLayerDto,
  OtcLayerInvoiceFeeInput,
  OtcLayerInvoiceFeeInputRefined,
  useUpdateOtcLayerInvoiceFee,
  useDeleteOtcLayerInvoiceFee,
} from "@/lib/api/otc";
import {
  OtcLayerInvoiceFeeForm,
  OtcLayerInvoiceFeeFormErrors,
  layerToInvoiceFeeValues,
  formInvoiceFeeValuesToApi,
} from "./OtcLayerInvoiceFeeForm";
import { LayerEconomicsSummary } from "./LayerEconomicsSummary";
import { generateAmendmentTitleForLayer } from "./common";

/** Maps zod issues onto the per-field error map used by the form. */
function issuesToErrors(
  issues: { path: (string | number)[]; message: string }[],
): OtcLayerInvoiceFeeFormErrors {
  const errors: OtcLayerInvoiceFeeFormErrors = {};
  for (const issue of issues) {
    const key = issue.path[0];
    if (typeof key === "string" && !(key in errors)) {
      errors[key as keyof OtcLayerInvoiceFeeFormErrors] = issue.message;
    }
  }
  return errors;
}

interface UpdateInvoiceFeeModalProps {
  open: boolean;
  contract_id: string;
  layer: OtcContractLayerDto | null;
  onClose: () => void;
  onUpdated: () => void;
  onDeleted: () => void;
}

export function UpdateInvoiceFeeModal({
  open,
  contract_id,
  layer,
  onClose,
  onUpdated,
  onDeleted,
}: UpdateInvoiceFeeModalProps) {
  const updateFee = useUpdateOtcLayerInvoiceFee(contract_id);
  const deleteFee = useDeleteOtcLayerInvoiceFee(contract_id);

  const [values, setValues] = useState<OtcLayerInvoiceFeeInput | null>(null);
  const [errors, setErrors] = useState<OtcLayerInvoiceFeeFormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // Seed from the layer's existing fee each time the modal opens.
  useEffect(() => {
    if (!open || !layer) return;
    setValues(layerToInvoiceFeeValues(layer));
    setErrors({});
    setSubmitError(null);
    setConfirmingDelete(false);
  }, [open, layer]);

  const busy = isSaving || isDeleting;

  const handleSubmit = async () => {
    if (busy || !layer || !values) return;
    setSubmitError(null);

    const parsed = OtcLayerInvoiceFeeInputRefined.safeParse(formInvoiceFeeValuesToApi(values));
    if (!parsed.success) {
      setErrors(issuesToErrors(parsed.error.issues));
      return;
    }
    setErrors({});

    setIsSaving(true);
    try {
      const result = await updateFee(layer.id, parsed.data);
      if (!result.success) {
        setSubmitError(result.message || "Failed to update invoice fee.");
        return;
      }
      onUpdated();
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (busy || !layer) return;
    setSubmitError(null);
    setIsDeleting(true);
    try {
      const result = await deleteFee(layer.id);
      if (!result.success) {
        setSubmitError(result.message || "Failed to delete invoice fee.");
        return;
      }
      onDeleted();
      onClose();
    } finally {
      setIsDeleting(false);
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (busy) return;
    if (!next) onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-none w-[50vw] max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>Update Invoice Fee</DialogTitle>
        </DialogHeader>

        <div className="grow overflow-y-auto pr-1 flex flex-col gap-4">
          {submitError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Something went wrong</AlertTitle>
              <AlertDescription>{submitError}</AlertDescription>
            </Alert>
          )}

          {layer && values && (
            <div className="rounded-lg border p-4 flex flex-col gap-3">
              <span className="font-medium">
                Layer #{layer.layer_no} — {generateAmendmentTitleForLayer(layer)}
              </span>

              <LayerEconomicsSummary layer={layer} />

              <OtcLayerInvoiceFeeForm
                values={values}
                onChange={setValues}
                errors={errors}
              />
            </div>
          )}
        </div>

        <DialogFooter className="shrink-0 flex sm:justify-between gap-2">
          <div className="flex items-center gap-2">
            {confirmingDelete ? (
              <>
                <span className="text-sm text-muted-foreground">
                  Delete this invoice fee?
                </span>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={busy}
                >
                  {isDeleting ? "Deleting..." : "Confirm Delete"}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setConfirmingDelete(false)}
                  disabled={busy}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <Button
                type="button"
                variant="destructive"
                onClick={() => setConfirmingDelete(true)}
                disabled={busy}
              >
                Delete
              </Button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleSubmit} disabled={busy}>
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
